import React, { useEffect } from 'react'
import { areArraysEqual } from '../../../_shared/lib'

export const useLogOnChange = (name: string, value: unknown) => {
  useEffect(() => {
    console.log(`OnChange [${name}]`, value)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps
}

export const useLoggingEffect = (effect: React.EffectCallback, deps: React.DependencyList, name: string): void => {
  const previousDeps = React.useRef<React.DependencyList>()

  React.useEffect(() => {
    const changedDeps = deps.map((dep, i) => ({ dep, i })).filter(({ dep, i }) => dep !== previousDeps.current?.[i])
    if (deps.length > 0 && changedDeps.length === 0) {
      return
    }
    console.log(`[DEBUG:useEffect] ${name} Executed`, { changedDeps, deps, previousDeps: previousDeps.current })
    previousDeps.current = deps
    return effect()
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
}

export const useLoggingMemo = <T>(factory: () => T, deps: React.DependencyList, name: string): T => {
  const previousDeps = React.useRef<React.DependencyList>()
  const value = React.useMemo(factory, deps) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const changedDeps = deps.map((dep, i) => ({ dep, i })).filter(({ dep, i }) => dep !== previousDeps.current?.[i])
    if (deps.length > 0 && changedDeps.length === 0) {
      return
    }
    console.log(`[DEBUG:useMemo] ${name} Changed`, { changedDeps, deps, previousDeps: previousDeps.current, value })
    previousDeps.current = deps
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
  return value
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useLoggingCallback = <T extends (...args: any[]) => any>(callback: T, deps: React.DependencyList, name: string): T => {
  const previousDeps = React.useRef<React.DependencyList>()
  const value = React.useCallback(callback, deps) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const changedDeps = deps.map((dep, i) => ({ dep, i })).filter(({ dep, i }) => dep !== previousDeps.current?.[i])
    if (deps.length > 0 && changedDeps.length === 0) {
      return
    }
    console.log(`[DEBUG:useCallback] ${name} Changed`, { changedDeps, deps, previousDeps: previousDeps.current })
    previousDeps.current = deps
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
  return value
}

export const useExecOnlyOnce = (effect: () => void, name: string): void => {
  const executedRef = React.useRef(false)
  React.useEffect(() => {
    if (!executedRef.current) {
      console.log(`[DEBUG:useExecOnlyOnce] ${name} Executed`, { executedRef: executedRef.current })
      effect()
    }
    executedRef.current = true
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}

type ElementType<T> = T extends (infer U)[] ? U : never

export const useMemoizedArray = <T extends unknown[] | null | undefined>(
  array: T,
  comparison?: T extends unknown[] ? (a: ElementType<T>, b: ElementType<T>) => boolean : never,
): T => {
  const ref = React.useRef<T>(array)

  const isArray = Array.isArray(array) && Array.isArray(ref.current)
  // array, ref.currentが配列の場合のみareArraysEqualを実行
  if (isArray) {
    if (!areArraysEqual(array as ElementType<T>[], ref.current as ElementType<T>[], comparison as (a: ElementType<T>, b: ElementType<T>) => boolean)) {
      ref.current = array
    }
  } else {
    // nullやundefinedの場合は、単純な比較と代入のみ
    if (ref.current !== array) {
      ref.current = array
    }
  }
  return React.useMemo<T>(() => ref.current, [ref.current]) // eslint-disable-line react-hooks/exhaustive-deps
}
